import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@astro/api'
import { useSocket, useSocketEvent } from '@astro/socket'
import {
  LuArrowLeft, LuBellRing, LuCalendarDays, LuLoaderCircle, LuMic, LuMicOff, LuPhone, LuPhoneOff,
  LuPlay, LuShieldCheck, LuShoppingBag, LuUserRound, LuVideo, LuVideoOff,
} from 'react-icons/lu'
import { useToast } from '../../../context/ToastContext'
import {
  astrologerDisplayName, formatDuration, formatRate, statusMeta, birthFacts, scheduleFacts, isNoteFinalized,
} from '../bookingDetails/bookingDetailsData'
import SuggestProductCard from './SuggestProductCard'
import {
  BackButton, CallShell, ControlButton, CountdownPill, HostNotesCard, HostSideCard, ProviderBadge,
  ScreenShareButton, SideFact,
} from './consultationShared'

/**
 * The consultation room — what both parties see once inside, on WHICHEVER
 * video provider the server locked the booking to. Everything that is the
 * consultation (countdown, ring-the-seeker, the consultation:* socket
 * signalling, stamping the booking live, the astrologer's side tools, leave vs
 * end) lives here once; everything that is the SDK arrives through `media`, an
 * adapter built by VideosdkConsultationRoom / LiveKitConsultationRoom:
 *
 *   media = {
 *     phase: 'lobby' | 'joining' | 'joined',
 *     join(), leave(), endForAll(),                  // endForAll = host closes the room
 *     registerHandlers({ onJoined, onLeft, onError, onRemoteEnd }),
 *     localParticipantId, remoteParticipantId, presenterId,
 *     localMicOn, localWebcamOn, localScreenShareOn,
 *     toggleMic(), toggleWebcam(), toggleScreenShare(),
 *     clock: { anchorMs, start() },                  // the shared session clock
 *     Tile, ShareTile, RemoteAudio?, DeviceSettingsButton,
 *   }
 *
 * `role` is server-signed now ('host' for the booking's astrologer, 'seeker'
 * for its customer). `counterpartId` is the other party's user id for the
 * socket; `onEndCall` (host) asks the server to close the room for everyone.
 */
export default function ConsultationRoomView({
  booking, role, backPath, audioOnly = false, provider, media, counterpartId, onEndCall,
}) {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { emit, isConnected } = useSocket()

  const durationMin = Number(booking.duration) > 0 ? Number(booking.duration) : 30
  const [remaining, setRemaining] = useState(durationMin * 60)
  const endedRef = useRef(false)
  const { phase, remoteParticipantId, localParticipantId } = media
  const anchorMs = media.clock.anchorMs

  // Marks a live session this seeker may rejoin (Join button on the booking
  // page + room admission), even when the backend rejected the status PATCH.
  // Set on join, kept on Leave, cleared when the session actually ends.
  const liveFlagKey = `live-consultation-${booking.id}`

  const finishCall = (reason) => {
    if (endedRef.current) return
    endedRef.current = true
    sessionStorage.removeItem(liveFlagKey)
    if (counterpartId) {
      emit('consultation:end', { toUserId: counterpartId, bookingId: booking.id })
    }
    if (role === 'host') {
      // Close the room for everyone: at the provider through the server (what
      // disconnects a seeker still in a LiveKit room), and from this client.
      // The booking is NOT marked completed here — the astrologer finalizes the
      // session from the appointment page, and that finalize is what flips it
      // to completed.
      onEndCall?.()
      try { media.endForAll() } catch { /* already gone */ }
      // Products picked during the call stay staged — ending the room writes
      // nothing to the customer's cart. They are published from the
      // appointment page's Confirm Finalize, together with the note, and the
      // staging store carries them across this navigation.
      showToast(
        reason
          || 'Session ended. Finalize your note to mark this appointment complete and share your product suggestions.',
      )
    } else {
      try { media.leave() } catch { /* already gone */ }
      if (reason) showToast(reason)
    }
    navigate(backPath)
  }
  const finishRef = useRef(finishCall)
  finishRef.current = finishCall

  // Seeker's exit: leaves the room without ending the session — the
  // astrologer stays in the call and can ring them back in.
  const leaveMeeting = () => {
    if (endedRef.current) return
    endedRef.current = true
    try { media.leave() } catch { /* already gone */ }
    navigate(backPath)
  }

  // The SDK's lifecycle callbacks, read by the adapter through a ref so they
  // always see this render's closures.
  media.registerHandlers({
    onJoined: () => { if (role === 'seeker') sessionStorage.setItem(liveFlagKey, '1') },
    onLeft: () => {
      if (!endedRef.current) navigate(backPath)
      endedRef.current = true
    },
    onError: (err) => showToast(err?.message || 'Video connection error.'),
    onRemoteEnd: () => finishRef.current('The consultation has ended.'),
  })

  // Session countdown — runs once the session is live (anchored), auto-ends
  // the call for both parties at zero.
  useEffect(() => {
    if (phase !== 'joined' || anchorMs == null) return undefined
    const total = durationMin * 60
    const tick = () => {
      const left = total - Math.floor((Date.now() - anchorMs) / 1000)
      setRemaining(Math.max(0, left))
      if (left <= 0) {
        clearInterval(timer)
        finishRef.current('Session time is over — the consultation has ended.')
      }
    }
    const timer = setInterval(tick, 1000)
    tick()
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, anchorMs])

  // Ring the seeker over the socket. The initial ring from the details page
  // can be lost if the socket was momentarily disconnected — so while the
  // host waits alone in the room, re-ring on every (re)connection, throttled.
  const lastRingRef = useRef(0)
  const ringSeeker = (force = false) => {
    if (role !== 'host' || !counterpartId) return false
    const now = Date.now()
    if (!force && now - lastRingRef.current < 10_000) return false
    lastRingRef.current = now
    // The socket reaches open browser tabs only; the phone hears /call/ring.
    api.bookings.ringCall(booking.id).catch(() => {})
    return emit('consultation:invite', { toUserId: counterpartId, bookingId: booking.id })
  }
  useEffect(() => {
    if (role !== 'host' || phase !== 'joined' || !isConnected || endedRef.current) return
    if (remoteParticipantId) return // seeker already here
    ringSeeker()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, phase, isConnected, remoteParticipantId])

  // The other side hung up / the seeker declined.
  useSocketEvent('consultation:end', (payload) => {
    if (String(payload?.bookingId) !== String(booking.id)) return
    finishRef.current('The consultation has ended.')
  })
  useSocketEvent('consultation:decline', (payload) => {
    if (role !== 'host' || String(payload?.bookingId) !== String(booking.id)) return
    showToast('The customer declined the video call.')
  })

  // Screen sharing — one presenter at a time. `presenterId` is whoever's
  // screen is live (null when nobody's sharing); while a screen is shared it
  // takes over the main stage and both cameras shrink to corner tiles.
  // Disabled for audio-only sessions, and for either side while the OTHER
  // party is already presenting.
  const someoneElsePresenting = Boolean(media.presenterId) && media.presenterId !== localParticipantId
  const presenterId = audioOnly ? null : media.presenterId
  const screenShareDisabled = audioOnly || someoneElsePresenting
  const screenShareTitle = audioOnly
    ? 'Screen sharing is disabled for audio consultations'
    : someoneElsePresenting
      ? 'The other participant is sharing their screen'
      : media.localScreenShareOn
        ? 'Stop sharing your screen'
        : 'Share your screen'

  // The session clock starts only once BOTH parties are in the room together
  // (not on each side's own join). At that same moment the seeker stamps the
  // booking live (in_progress + meeting_started_at), so reloads and the
  // astrologer's side count down from one shared anchor.
  const stampedLiveRef = useRef(false)
  useEffect(() => {
    if (phase !== 'joined' || !remoteParticipantId) return
    if (
      role === 'seeker' &&
      !stampedLiveRef.current &&
      // The server already moves the booking to in_progress when the seeker
      // opens the room, so the status can't tell whether the clock was stamped.
      !booking.meeting_started_at
    ) {
      stampedLiveRef.current = true
      api.bookings.joinVideo(booking.id).catch(() => {})
    }
    media.clock.start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, remoteParticipantId])

  const { Tile, ShareTile, RemoteAudio, DeviceSettingsButton } = media

  const controls = (
    <>
      <ControlButton
        active={media.localMicOn}
        onClick={() => media.toggleMic()}
        OnIcon={LuMic}
        OffIcon={LuMicOff}
        title={media.localMicOn ? 'Mute microphone' : 'Unmute microphone'}
      />
      <ControlButton
        active={media.localWebcamOn}
        disabled={audioOnly}
        onClick={() => { if (!audioOnly) media.toggleWebcam() }}
        OnIcon={LuVideo}
        OffIcon={LuVideoOff}
        title={audioOnly ? 'Video is disabled for audio consultations' : media.localWebcamOn ? 'Turn camera off' : 'Turn camera on'}
      />
      <ScreenShareButton
        sharing={media.localScreenShareOn}
        disabled={screenShareDisabled}
        title={screenShareTitle}
        onClick={() => media.toggleScreenShare()}
      />
      <DeviceSettingsButton />
    </>
  )

  const stage = (waitingFor) => (
    <>
      {/* Remote voice on one persistent player, independent of the tile
          layout — never drops on a screen-share toggle. (LiveKit renders all
          remote audio at the room level instead, so the adapter passes none.) */}
      {RemoteAudio && remoteParticipantId && <RemoteAudio participantId={remoteParticipantId} />}
      {presenterId ? (
        <ShareTile participantId={presenterId} />
      ) : remoteParticipantId ? (
        <Tile participantId={remoteParticipantId} fill audioOnly={audioOnly} />
      ) : (
        <div className="h-full rounded-2xl border border-dashed border-white/15 flex flex-col items-center justify-center text-center p-4 sm:p-8">
          <LuLoaderCircle className="w-8 h-8 sm:w-10 sm:h-10 mb-3 text-[#E6C878]/40 animate-spin" />
          <p className="text-xs sm:text-sm text-white/50">Waiting for {waitingFor} to join…</p>
          {audioOnly && (
            <p className="inline-flex items-center gap-1.5 mt-2 text-[10px] uppercase tracking-wider text-[#E6C878]/70">
              <LuPhone className="w-3 h-3" /> Audio-only session — video is disabled
            </p>
          )}
          {role === 'host' && (
            <button
              type="button"
              onClick={() => {
                const sent = ringSeeker(true)
                showToast(sent ? 'Invitation sent to the customer.' : 'Realtime connection is offline — reconnecting…')
              }}
              className="mt-4 sm:mt-5 inline-flex items-center gap-2 rounded-full px-4 sm:px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.2em] border border-[#E6C878]/40 text-[#E6C878] hover:bg-[#E6C878]/10 transition-colors"
            >
              <LuBellRing className="w-3.5 h-3.5" /> Ring Customer Again
            </button>
          )}
        </div>
      )}
      {/* Camera tiles ride the lower corner; while a screen is shared the
          remote face joins the self-view here so the shared content keeps the
          whole stage. Kept narrow on phones — a w-36 thumbnail is a third of a
          360px stage. */}
      {!audioOnly && (localParticipantId || remoteParticipantId) && (
        <div className="absolute bottom-3 right-3 sm:bottom-6 sm:right-6 z-10 flex flex-col gap-2 sm:gap-3 w-24 sm:w-40 md:w-44 lg:w-56">
          {presenterId && remoteParticipantId && (
            <Tile participantId={remoteParticipantId} compact />
          )}
          {localParticipantId && (
            <Tile participantId={localParticipantId} compact />
          )}
        </div>
      )}
    </>
  )

  /* ── Astrologer (host) console: 7:3 split — video left, tools right ── */
  if (role === 'host') {
    const joined = phase === 'joined'
    const status = statusMeta(booking.status)
    return (
      <div className="min-h-dvh bg-[#14100D] flex flex-col">
        {/* Header: identity + status + countdown */}
        <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 sm:px-6 py-3 sm:py-3.5 border-b border-white/10">
          <div className="min-w-0">
            <p className="flex items-center gap-2 font-cinzel text-base sm:text-lg text-[#E6C878] leading-tight">
              {audioOnly ? <LuPhone className="w-4 h-4 shrink-0" /> : <LuVideo className="w-4 h-4 shrink-0" />}
              {audioOnly ? 'Audio Consultation' : 'Video Consultation'}
              <ProviderBadge provider={provider} />
            </p>
            <p className="text-[10px] uppercase tracking-widest text-white/35 mt-1 wrap-break-word">
              Appointment BKNG{booking.id} · with {booking.name || 'Customer'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* A live status dot rather than STATUS_META's emoji — that table is
                shared with the booking/appointment pages, so it stays as it is. */}
            <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider px-2.5 sm:px-3 py-1.5 rounded-full border border-[#C8922A]/30 bg-[#C8922A]/10 text-[#E6C878]">
              <span className={`w-1.5 h-1.5 rounded-full bg-[#E6C878] ${joined ? 'animate-pulse' : 'opacity-50'}`} />
              {status.label}
            </span>
            <CountdownPill remaining={remaining} />
            {!joined && (
              <button
                type="button"
                onClick={() => navigate(backPath)}
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[9px] font-bold uppercase tracking-wider border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-colors"
              >
                <LuArrowLeft className="w-3 h-3" /> Back to Appointment
              </button>
            )}
          </div>
        </header>

        {/* 7:3 split — video console + astrologer tools */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-10 gap-3 sm:gap-4 p-3 sm:p-5 items-start">
          {/* Video panel. The height is DEFINITE at every breakpoint: the stage
              below is a flex-1 box whose tile sizes itself with h-full, and a
              percentage height needs a definite height up the chain or it
              collapses — the same trap the seeker's h-dvh root note describes. */}
          <section className="lg:col-span-7 flex flex-col rounded-2xl overflow-hidden bg-[#1F1913] border border-white/10 h-[58vh] sm:h-[62vh] lg:h-[calc(100vh-140px)]">
            {joined ? (
              <>
                <div className="relative flex-1 min-h-0 p-2 sm:p-3">
                  {stage(booking.name || 'the customer')}
                </div>
                {/* flex-wrap, not a fixed row: three 52px controls plus a
                    tracking-[0.2em] "End Consultation" pill ran ~370px wide and
                    overflowed a 360px phone. */}
                <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 px-3 sm:px-5 py-3 sm:py-3.5 border-t border-white/10">
                  {controls}
                  <button
                    type="button"
                    onClick={() => finishCall()}
                    className="inline-flex items-center gap-2 rounded-full px-5 sm:px-8 py-3 sm:py-3.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.2em] bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    <LuPhoneOff className="w-4 h-4 shrink-0" />
                    <span className="hidden sm:inline">End Consultation</span>
                    <span className="sm:hidden">End</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-6 py-10 sm:py-14">
                {audioOnly
                  ? <LuPhone className="w-10 h-10 sm:w-12 sm:h-12 mb-4 sm:mb-5 text-[#E6C878]" />
                  : <LuVideo className="w-10 h-10 sm:w-12 sm:h-12 mb-4 sm:mb-5 text-[#E6C878]" />}
                <p className="font-cinzel text-xl sm:text-2xl text-white mb-2">Start the consultation</p>
                <p className="text-xs sm:text-sm text-white/50 mb-6 sm:mb-8">
                  {booking.name || 'Your customer'} has been invited — join to open the room.
                </p>
                {audioOnly && (
                  <p className="inline-flex items-center gap-1.5 -mt-4 mb-6 sm:mb-8 text-[10px] uppercase tracking-wider text-[#E6C878]/70">
                    <LuPhone className="w-3 h-3" /> Audio-only session — video is disabled
                  </p>
                )}
                <button
                  type="button"
                  disabled={phase === 'joining'}
                  onClick={() => media.join()}
                  className="hero-btn-primary inline-flex items-center gap-2 rounded-full px-6 sm:px-10 py-3.5 sm:py-4 text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.2em] disabled:opacity-60"
                >
                  {phase === 'joining'
                    ? <><LuLoaderCircle className="w-3.5 h-3.5 animate-spin" /> Connecting…</>
                    : <><LuPlay className="w-3.5 h-3.5" /> Join Consultation</>}
                </button>
              </div>
            )}
            <p className="flex items-center gap-1.5 px-3 sm:px-5 py-2.5 text-[10px] text-white/30 border-t border-white/10">
              <LuShieldCheck className="w-3 h-3 shrink-0" /> Your session is encrypted and secure.
            </p>
          </section>

          {/* Astrologer tools */}
          <aside className="lg:col-span-3 space-y-3 sm:space-y-4 lg:h-[calc(100vh-140px)] lg:overflow-y-auto">
            <HostSideCard title="Customer Details" icon={<LuUserRound className="w-3.5 h-3.5" />}>
              <SideFact label="Name" value={booking.name} />
              <SideFact label="Email" value={booking.email} />
              <SideFact label="Contact" value={booking.phone} />
              <SideFact label="Type" value={booking.consultation_type} />
              <SideFact label="Birth Date" value={birthFacts(booking).date} />
              <SideFact label="Birth Time" value={birthFacts(booking).time} />
              <SideFact label="Birth Place" value={birthFacts(booking).place} />
            </HostSideCard>

            <HostSideCard title="Appointment" icon={<LuCalendarDays className="w-3.5 h-3.5" />}>
              <SideFact label="Date" value={scheduleFacts(booking).dateLabel} />
              <SideFact label="Slot" value={scheduleFacts(booking).slotLabel} />
              <SideFact label="Duration" value={formatDuration(booking.duration)} />
              <SideFact label="Rate" value={formatRate(booking.rate)} />
            </HostSideCard>

            <HostNotesCard booking={booking} />

            {/* In-call product suggestions — same engine as the appointment
                page's panel, but staged: picks reach the customer's cart in
                one batch when the consultation ends, not as they are made. */}
            <HostSideCard title="Suggest Product" icon={<LuShoppingBag className="w-3.5 h-3.5" />} collapsible>
              <SuggestProductCard booking={booking} disabled={isNoteFinalized(booking)} />
            </HostSideCard>
          </aside>
        </div>
      </div>
    )
  }

  /* Seeker pre-join: the room joins itself — this is just the connecting
     state, not a second confirmation. */
  if (phase !== 'joined') {
    return (
      <CallShell>
        {audioOnly
          ? <LuPhone className="w-12 h-12 mb-5 text-[#E6C878] animate-pulse" />
          : <LuVideo className="w-12 h-12 mb-5 text-[#E6C878] animate-pulse" />}
        <p className="font-cinzel text-xl sm:text-2xl text-white mb-2">Joining the consultation…</p>
        <p className="text-sm text-white/50 mb-1">
          BKNG{booking.id} · {formatDuration(durationMin)} session
        </p>
        <p className="text-xs text-white/40 mb-8">
          With {astrologerDisplayName(booking)} — connecting you now.
        </p>
        <BackButton onClick={() => navigate(backPath)} label="Back to Details" />
      </CallShell>
    )
  }

  return (
    // A DEFINITE viewport height, not min-h-*: a shared-screen player renders a
    // percentage-height <video> that needs a definite height up the chain to
    // fit — under min-h-screen it fell back to the stream's native resolution
    // and pushed the page taller than the viewport. overflow-hidden is the
    // belt-and-braces. h-dvh, not h-screen: on mobile 100vh is the height with
    // the browser chrome RETRACTED, so the control row sat behind the address
    // bar until you scrolled — and overflow-hidden meant you couldn't.
    <div className="h-dvh overflow-hidden bg-[#14100D] flex flex-col">
      {/* Top bar: identity + countdown */}
      <div className="flex items-center justify-between gap-3 sm:gap-4 px-3 sm:px-5 py-2.5 sm:py-3.5 border-b border-white/10">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-cinzel text-xs sm:text-sm text-[#E6C878] truncate">
            Consultation with {astrologerDisplayName(booking)}
            <ProviderBadge provider={provider} />
          </p>
          <p className="text-[10px] uppercase tracking-widest text-white/35 mt-0.5">BKNG{booking.id}</p>
        </div>
        <CountdownPill remaining={remaining} />
      </div>

      {/* Stage: astrologer fills the frame, own camera in the lower corner */}
      <div className="relative flex-1 min-h-0 p-2 sm:p-5">
        {stage(astrologerDisplayName(booking))}
      </div>

      {/* Controls — devices + leave. Leaving only exits this seat: the
          session itself ends when the astrologer closes it or the timer
          runs out. */}
      <div className="flex flex-col items-center gap-2 px-3 sm:px-5 py-3 sm:py-4 border-t border-white/10">
        <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-4">
          {controls}
          <button
            type="button"
            onClick={leaveMeeting}
            className="inline-flex items-center gap-2 rounded-full px-5 sm:px-8 py-3 sm:py-3.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.2em] border border-red-400/50 text-red-300 hover:bg-red-500/15 transition-colors"
          >
            <LuPhoneOff className="w-4 h-4 shrink-0" /> Leave
          </button>
        </div>
        <p className="text-[10px] text-white/30 text-center px-2">
          Leaving won&rsquo;t end the session — you can rejoin from your booking page while it&rsquo;s live.
        </p>
      </div>
    </div>
  )
}
